import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import {
  AppBar, Toolbar, Typography, Button, Table, TableHead, TableBody,
  TableRow, TableCell, CircularProgress, CssBaseline, IconButton, Box,
  TableFooter,
} from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';

import {
  PendingActions as PendingActionsIcon,
  ExitToApp as ExitToAppIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Widgets as WidgetsIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';

import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, PAGE_PADDING, CONTROL_HEIGHT,
  ELLIPSIS_CSS, humanizeStatus,
} from '../../styles/recruiterTheme';
import Pagination from '../../utils/Pagination';
import { getEmailTemplate } from '../../emailTemplates/emailTemplate';
import ProfileForm from '../shared/Profileform.js';
import logo from '../../styles/logo.png';
import PendingData from '../shared/PendingData.js';
import Reports from '../shared/Reports';
import ActivityLogs from "./ActivityLogs.js";

// ─── Global ──────────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
`;

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Toast Notification ───────────────────────────────────────────────────────
const toastSlideIn = keyframes`
  from { opacity: 0; transform: translateX(110%); }
  to   { opacity: 1; transform: translateX(0); }
`;

const toastSlideOut = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to   { opacity: 0; transform: translateX(110%); }
`;

const ToastWrapper = styled.div`
  position: fixed;
  top: 88px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
`;

const Toast = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${RADIUS.md}px;
  min-width: 300px;
  max-width: 400px;
  background: ${p =>
    p.type === 'success' ? 'linear-gradient(135deg, #1B6B3A, #2E9B54)' :
    p.type === 'error'   ? 'linear-gradient(135deg, #B71C1C, #E53935)' :
    p.type === 'warning' ? 'linear-gradient(135deg, #BF360C, #E65100)' :
    'linear-gradient(135deg, #4F378B, #6750A4)'};
  color: #fff;
  box-shadow: 0 8px 28px rgba(0,0,0,0.18);
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  line-height: 1.5;
  pointer-events: all;
  cursor: default;
  animation: ${p => p.$exiting ? css`${toastSlideOut} 0.35s ease forwards` : css`${toastSlideIn} 0.35s ease both`};

  svg { font-size: 20px; flex-shrink: 0; }
`;

const ToastClose = styled(IconButton)`
  && {
    color: rgba(255,255,255,0.75) !important;
    padding: 2px !important;
    margin-left: auto !important;
    &:hover { color: #fff !important; background: rgba(255,255,255,0.15) !important; }
    svg { font-size: 16px; }
  }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Container = styled.div`
  display: flex;
  background: ${COLORS.background};
  min-height: 100vh;
  font-family: ${FONT_BODY};
  padding-top: 72px;
  overflow: hidden;
`;

const CenteredSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
  height: calc(100vh - 72px);
  background: ${COLORS.background};
`;

const ScrollableContent = styled.div`
  width: 100%;
  min-height: 100%;
  padding-bottom: 40px;
`;

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 300px;
  min-width: 260px;
  flex-shrink: 0;
  background: linear-gradient(170deg, #2D1B5E 0%, #4F378B 55%, #6750A4 100%);
  height: calc(100vh - 72px);
  padding: 24px 16px;
  position: sticky;
  top: 0;
  overflow-y: auto;
  box-shadow: 4px 0 24px rgba(79,55,139,0.18);
  z-index: 10;
`;

const SidebarLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY};
    font-size: ${TYPE.overline}px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 1.25;
    text-transform: uppercase;
    color: rgba(255,255,255,0.45);
    margin: 16px 0 8px 12px;
  }
`;

const NavButton = styled(IconButton)`
  && {
    width: 100%;
    border-radius: ${RADIUS.sm}px;
    padding: 10px 12px;
    justify-content: flex-start;
    text-align: left;
    color: rgba(255,255,255,0.82);
    transition: background 0.18s ease, color 0.18s ease;
    margin-bottom: 4px;
    gap: 12px;
    font-family: ${FONT_BODY};
    font-weight: 600;
    font-size: ${TYPE.body}px;
    line-height: 1.25;
    position: relative;
    ${p => p.$active ? 'background: rgba(255,255,255,0.18); color: #fff;' : ''}
    &:hover {
      background: rgba(255,255,255,0.14);
      color: #fff;
    }
    svg { font-size: 20px; flex-shrink: 0; }
  }
`;

const SidebarDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  margin: 12px 0;
`;

// ─── AppBar ───────────────────────────────────────────────────────────────────
const StyledAppBar = styled(AppBar)`
  && {
    background: rgba(255,255,255,0.97) !important;
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(103,80,164,0.12) !important;
    box-shadow: 0 2px 20px rgba(79,55,139,0.08) !important;
    left: 0 !important;
    width: 100% !important;
    z-index: 1300 !important;
  }
`;

const BrandText = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 800 !important;
    font-size: ${TYPE.section}px !important;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.02em;
    line-height: 1.25;
  }
`;

const StyledAvatar = styled.div`
  font-family: ${FONT_BODY};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
  color: #fff;
  font-size: ${TYPE.body}px;
  font-weight: 700;
  border-radius: 50%;
  box-shadow: 0 3px 10px rgba(79,55,139,0.35);
  flex-shrink: 0;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 16px 4px 4px;
  background: linear-gradient(135deg, #F8F4FF, #EDE7F6);
  border-radius: ${RADIUS.pill}px;
  border: 1px solid rgba(103,80,164,0.15);
  min-width: 160px;
  white-space: nowrap;
`;

// ─── Page shell ("cards on canvas") ───────────────────────────────────────────
// Transparent container on the lavender canvas; every tab renders inside it and
// embedded tab components add no outer padding of their own.
const StyledPaper = styled.div`
  padding: ${PAGE_PADDING}px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
`;

const TableCard = styled.div`
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  min-width: 0;
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid ${COLORS.border};
`;

const CellEllipsis = styled.div`
  ${ELLIPSIS_CSS}
  max-width: ${p => p.maxWidth || 320}px;
`;

// ─── Page Title ───────────────────────────────────────────────────────────────
const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
    margin: 4px 0 24px !important;
  }
`;

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg, #6750A4, #4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 96px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 24px ${p => p.shadow || 'rgba(103,80,164,0.28)'};
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px ${p => p.shadow || 'rgba(103,80,164,0.36)'};
  }
  &::after {
    content: '';
    position: absolute;
    top: -30px; right: -30px;
    width: 90px; height: 90px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const StatNumber = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.22;
  svg { font-size: 40px; }
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const StyledTable = styled(Table)`
  && { border-collapse: separate; border-spacing: 0; }
  && tbody tr:last-child td { border-bottom: none !important; }
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
    border-bottom: none !important;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.16s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: ${COLORS.background} !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: ${COLORS.ink} !important;
    padding: 12px 16px !important;
    vertical-align: middle;
    border-bottom: 1px solid #F0EAF9 !important;
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const ActionButton = styled(Button)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: ${CONTROL_HEIGHT.sm}px;
    padding: 0 12px !important;
    white-space: nowrap;
    text-transform: none !important;
    transition: background 0.18s ease !important;
    border: 1px solid ${p => p.$danger ? 'rgba(179,38,30,0.35)' : 'rgba(103,80,164,0.35)'} !important;
    color: ${p => p.$danger ? COLORS.error : COLORS.primaryDark} !important;
    &:hover {
      background: ${p => p.$danger ? 'rgba(179,38,30,0.06)' : 'rgba(103,80,164,0.08)'} !important;
    }
    &.Mui-disabled { opacity: 0.5; }
  }
`;

const GhostButton = styled(Button)`
  && {
    color: ${COLORS.primary} !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: ${CONTROL_HEIGHT.md}px;
    padding: 0 16px !important;
    border: 1px solid rgba(103,80,164,0.35) !important;
    &:hover { background: rgba(103,80,164,0.08) !important; }
  }
`;

// ─── Status Chips ─────────────────────────────────────────────────────────────
const StatusChip = styled.span`
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  line-height: 1.5;
  padding: 2px 12px;
  border-radius: ${RADIUS.pill}px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  text-transform: capitalize;
  background: ${p => p.bg || COLORS.accentSoft};
  color: ${p => p.color || COLORS.primaryDark};
`;

// ─── Exported Handlers ───────────────────────────────────────────────────────
export const handleApprove = async (email, index, setLoadingMap, approveuserApiUrl, sendemailApiUrl, fetchUsers) => {
  try {
    const response = await axios.post(approveuserApiUrl, { email });
    if (response.data.success) {
      fetchUsers(response.data);
      await sendApprovalEmail(email, sendemailApiUrl);
    }
  } catch (error) {
    console.error('Error approving user:', error);
  } finally {
    setLoadingMap(prevLoadingMap => ({ ...prevLoadingMap, [index]: false }));
  }
};

export const handleReject = async (email, index, setLoadingMap, rejectuserApiUrl, sendemailApiUrl, fetchUsers) => {
  try {
    const response = await axios.post(rejectuserApiUrl, { email });
    if (response.data.success) {
      fetchUsers(response.data);
      await sendRejectionEmail(email, sendemailApiUrl);
    }
  } catch (error) {
    console.error('Error rejecting user:', error);
  } finally {
    setLoadingMap(prevLoadingMap => ({ ...prevLoadingMap, [index]: false }));
  }
};

export const sendApprovalEmail = async (email, sendemailApiUrl) => {
  try {
    const loginUrl = process.env.REACT_APP_LOCAL_HOST_API;
    const subject = "Your Role has been Approved!";
    const body = `
      <html>
        <body style="margin: 0; padding: 0;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #F5F5F5; color: #000000; text-align: center; padding: 20px;">
            <tr><td align="center"><table role="presentation" width="600" cellspacing="0" cellpadding="20" style="background-color: #FFFFFF; border: 1px solid #DDD; border-radius: 10px;">
            <tr><td style="background-color: #F8F1FA; color: #000000; text-align: center; padding: 10px;">
            <h2 style="margin: 0; font-size: 24px;">Resourcing Bot</h2></td></tr>
            <tr><td style="padding: 20px;">
            <p>Hi, congratulations! Your Resourcing Bot role has been approved by the admin.</p>
            <p>Please click the link below to access the login page:</p>
            <a href="${loginUrl}" style="display: inline-block; width: 195px; height: 40px; background-color: #68548E; color: white; text-align: center; font-size: 16px; font-weight: bold; line-height: 40px; text-decoration: none; border-radius: 5px;">
            Click here to login</a><p>Best regards,<br>Team</p></td></tr>
            <tr><td style="background-color: #4F378B; color: #FFFFFF; text-align: center; padding: 10px; border-radius: 0 0 10px 10px;">
            <p style="margin: 0; font-size: 14px;">Resourcing Bot</p>
            <a href="mailto:outsourcing@zentreelabs.com" style="color: #FFFFFF; text-decoration: underline;">outsourcing@zentreelabs.com</a></td></tr>
            </table></td></tr></table>
        </body>
      </html>`;
    await axios.post(sendemailApiUrl, { candidateEmail: [email], subject, body });
  } catch (error) {
    console.error('Error sending approval email:', error);
    throw new Error('Failed to send approval email');
  }
};

export const sendRejectionEmail = async (email, sendemailApiUrl) => {
  try {
    const { subject, body } = getEmailTemplate('rejectionEmail');
    await axios.post(sendemailApiUrl, { candidateEmail: [email], subject, body });
  } catch (error) {
    console.error('Error sending rejection email:', error);
    throw new Error('Failed to send rejection email');
  }
};

// ─── Session helpers ─────────────────────────────────────────────────────────
const TABS = new Set(['dashboard', 'approved', 'rejected', 'pendingData', 'report', 'activityLogs', 'profile']);
const DEFAULT_TAB = 'dashboard';
const TAB_KEY = 'activeTab';
const RETURN_TAB_KEY = 'returnTab';
// Login hands over approved/rejected lists only once and the backend has no
// endpoint for them, so a trimmed copy is kept for this tab to survive F5.
const LISTS_KEY = 'adminUserLists';
const PENDING_POLL_MS = 5000;
const ITEMS_PER_PAGE = 10;

const readSession = (key) => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};
const writeSession = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
};
const removeSession = (key) => {
  try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
};

const readStoredUser = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('rbotUser'));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

// Keep only what the tables show (the API returns whole user documents).
const trimUser = (u) => ({
  _id: u?._id, email: u?.email, name: u?.name, phone: u?.phone, userType: u?.userType,
});
const toList = (v) => (Array.isArray(v) ? v.filter(Boolean) : null);

const readCachedLists = () => {
  try {
    const parsed = JSON.parse(readSession(LISTS_KEY));
    if (!parsed || typeof parsed !== 'object') return null;
    return { approved: toList(parsed.approved), rejected: toList(parsed.rejected) };
  } catch {
    return null;
  }
};
const writeCachedLists = (approved, rejected) => {
  writeSession(LISTS_KEY, JSON.stringify({
    approved: approved.map(trimUser),
    rejected: rejected.map(trimUser),
  }));
};

const STATUS_STYLE = {
  'Under Review': { bg: COLORS.accentSoft, color: COLORS.primaryDark },
  Approved: { bg: COLORS.successSoft, color: COLORS.success },
  Rejected: { bg: COLORS.errorSoft, color: COLORS.error },
};

const StateBlock = ({ loading, message, onRetry, error }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, px: 3, gap: 1.5, textAlign: 'center' }}>
    {loading && <CircularProgress size={28} sx={{ color: COLORS.primary }} />}
    {message && (
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: error ? COLORS.error : COLORS.muted }}>
        {message}
      </Typography>
    )}
    {onRetry && <GhostButton onClick={onRetry}>Retry</GhostButton>}
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────
function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // User fields come from navigation state (fresh login) or, after a refresh,
  // from the profile Login.js persisted in localStorage.
  const currentUser = useMemo(
    () => (location.state?.email ? location.state : readStoredUser()) || {},
    [location.state]
  );
  const { email, name, phone, userType, _id } = currentUser;
  const [profileOverride, setProfileOverride] = useState(null);
  const userData = useMemo(() => {
    if (!email || !userType) return null;
    return { name: name || '', phone, email, userType, _id, ...(profileOverride || {}) };
  }, [name, phone, email, userType, _id, profileOverride]);

  useEffect(() => {
    if (!email) navigate('/login', { replace: true });
  }, [email, navigate]);

  // ─── User lists ───────────────────────────────────────────────────────────
  const initialLists = useMemo(() => {
    const st = location.state || {};
    const fromState = {
      pending: toList(st.initialPendingUsers),
      approved: toList(st.initialApprovedUsers),
      rejected: toList(st.initialRejectedUsers),
    };
    const cached = readCachedLists();
    return {
      pending: fromState.pending,
      approved: fromState.approved || cached?.approved || null,
      rejected: fromState.rejected || cached?.rejected || null,
    };
    // Only read once on mount; later updates come from the API.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pendingUsers, setPendingUsers] = useState(initialLists.pending || []);
  const [approvedUsers, setApprovedUsers] = useState(initialLists.approved || []);
  const [rejectedUsers, setRejectedUsers] = useState(initialLists.rejected || []);
  // false after a refresh with nothing cached: counts show "—" instead of 0.
  const [listsKnown, setListsKnown] = useState(Boolean(initialLists.approved && initialLists.rejected));
  const [pendingLoading, setPendingLoading] = useState(!initialLists.pending);
  const [pendingError, setPendingError] = useState('');
  const [busyMap, setBusyMap] = useState({});

  useEffect(() => {
    if (initialLists.approved && initialLists.rejected) {
      writeCachedLists(initialLists.approved, initialLists.rejected);
    }
  }, [initialLists]);

  const approveuserApiUrl = process.env.REACT_APP_APPROVE_USER_API;
  const sendemailApiUrl = process.env.REACT_APP_SEND_EMAIL_API || "http://127.0.0.1:3004/sendEmail";
  const rejectuserApiUrl = process.env.REACT_APP_REJECT_USER_API;
  const logoutApiUrl = process.env.REACT_APP_LOGOUT_API;
  const pendingusersUrl = process.env.REACT_APP_FETCH_PENDING_USERS_API;

  const [activeTab, setActiveTab] = useState(() => {
    const saved = readSession(TAB_KEY);
    return saved && TABS.has(saved) ? saved : DEFAULT_TAB;
  });
  const [tabRefreshKey, setTabRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { writeSession(TAB_KEY, activeTab); }, [activeTab]);

  // ─── Toast system ─────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef([]);

  useEffect(() => () => toastTimers.current.forEach(clearTimeout), []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    toastTimers.current.push(setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 380));
  }, []);

  const showToast = useCallback((message, type = 'success') => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts(prev => [...prev, { id, message, type, exiting: false }]);
    toastTimers.current.push(setTimeout(() => removeToast(id), 3500));
  }, [removeToast]);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const applyLists = useCallback((data) => {
    const pending = toList(data?.pendingUsers);
    const approved = toList(data?.approvedUsers);
    const rejected = toList(data?.rejectedUsers);
    if (pending) setPendingUsers(pending);
    if (approved) setApprovedUsers(approved);
    if (rejected) setRejectedUsers(rejected);
    if (approved && rejected) {
      setListsKnown(true);
      writeCachedLists(approved, rejected);
    }
  }, []);

  const pendingInFlight = useRef(false);
  const fetchPendingUsers = useCallback(async ({ silent = false } = {}) => {
    if (!pendingusersUrl || pendingInFlight.current) return;
    pendingInFlight.current = true;
    if (!silent) { setPendingLoading(true); setPendingError(''); }
    try {
      const response = await axios.get(pendingusersUrl);
      if (response.data?.success) {
        // The endpoint also returns approved/rejected lists, so a refresh restores every tab.
        applyLists({ ...response.data, pendingUsers: response.data.pendingUsers || [] });
        setPendingError('');
      } else if (!silent) {
        setPendingError('Could not load pending users.');
      }
    } catch (error) {
      console.error('Error fetching pending users:', error);
      if (!silent) setPendingError('Could not load pending users.');
    } finally {
      pendingInFlight.current = false;
      if (!silent) setPendingLoading(false);
    }
  }, [pendingusersUrl, applyLists]);

  useEffect(() => {
    if (!email) return undefined;
    fetchPendingUsers({ silent: Boolean(initialLists.pending) });
    const interval = setInterval(() => {
      if (!document.hidden) fetchPendingUsers({ silent: true });
    }, PENDING_POLL_MS);
    return () => clearInterval(interval);
  }, [email, fetchPendingUsers, initialLists]);

  // ─── Approve / reject ─────────────────────────────────────────────────────
  const decide = useCallback(async (userEmail, kind) => {
    if (!userEmail || busyMap[userEmail]) return;
    const approve = kind === 'approve';
    setBusyMap(prev => ({ ...prev, [userEmail]: true }));
    try {
      const response = await axios.post(approve ? approveuserApiUrl : rejectuserApiUrl, { email: userEmail });
      if (!response.data?.success) {
        showToast(`Failed to ${approve ? 'approve' : 'reject'} user. Please try again.`, 'error');
        return;
      }
      applyLists(response.data);
      try {
        if (approve) await sendApprovalEmail(userEmail, sendemailApiUrl);
        else await sendRejectionEmail(userEmail, sendemailApiUrl);
        showToast(`${userEmail} has been ${approve ? 'approved' : 'rejected'}.`, 'success');
      } catch {
        showToast(`${userEmail} was ${approve ? 'approved' : 'rejected'}, but the notification email could not be sent.`, 'warning');
      }
    } catch (error) {
      console.error(`Error ${approve ? 'approving' : 'rejecting'} user:`, error);
      showToast(`An error occurred while ${approve ? 'approving' : 'rejecting'} the user.`, 'error');
    } finally {
      setBusyMap(prev => {
        const next = { ...prev };
        delete next[userEmail];
        return next;
      });
    }
  }, [busyMap, approveuserApiUrl, rejectuserApiUrl, sendemailApiUrl, applyLists, showToast]);

  // ─── Navigation ───────────────────────────────────────────────────────────
  const switchTab = useCallback((tab) => {
    removeSession(RETURN_TAB_KEY);
    setCurrentPage(1);
    if (tab === 'dashboard') fetchPendingUsers({ silent: pendingUsers.length > 0 });
    setActiveTab(tab);
    setTabRefreshKey(k => k + 1);
  }, [fetchPendingUsers, pendingUsers.length]);

  const handleLogout = useCallback(async () => {
    const em = email || localStorage.getItem('email');
    let tokens = {};
    try {
      tokens = JSON.parse(localStorage.getItem('userTokens')) || {};
    } catch {
      tokens = {};
    }
    const storedToken = localStorage.getItem('token');
    const token = (em && tokens[em]) || (storedToken && storedToken !== 'null' ? storedToken : null);

    try {
      if (token && logoutApiUrl) {
        await axios.post(logoutApiUrl, { token, email: em }, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (error) {
      console.error('Error logging out:', error.response?.data?.message || error.message);
    } finally {
      if (em) delete tokens[em];
      localStorage.setItem('userTokens', JSON.stringify(tokens));
      localStorage.removeItem('token');
      localStorage.removeItem('email');
      localStorage.removeItem('rbotUser');
      removeSession(TAB_KEY);
      removeSession(RETURN_TAB_KEY);
      removeSession(LISTS_KEY);
      navigate('/login', { replace: true });
    }
  }, [email, logoutApiUrl, navigate]);

  // ─── Pagination ───────────────────────────────────────────────────────────
  let usersToPaginate = [];
  if (activeTab === 'dashboard') usersToPaginate = pendingUsers;
  else if (activeTab === 'approved') usersToPaginate = approvedUsers;
  else if (activeTab === 'rejected') usersToPaginate = rejectedUsers;

  const totalPages = Math.ceil(usersToPaginate.length / ITEMS_PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageRows = usersToPaginate.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const count = (list) => (listsKnown ? list.length : '—');

  // ─── Renderers ────────────────────────────────────────────────────────────
  const renderTable = (title, rows, statusLabel, actions) => {
    const chip = STATUS_STYLE[statusLabel] || {};
    return (
      <TableCard>
        <CardHeader>
          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.primaryDeep, lineHeight: 1.5 }}>
            {title}
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.muted, fontVariantNumeric: 'tabular-nums' }}>
            {usersToPaginate.length} {usersToPaginate.length === 1 ? 'user' : 'users'}
          </Typography>
        </CardHeader>
        <TableScroll>
          <StyledTable sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <HeadCell>User</HeadCell>
                <HeadCell>Role</HeadCell>
                <HeadCell>Status</HeadCell>
                <HeadCell align="right">Actions</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((user) => {
                const busy = Boolean(busyMap[user.email]);
                return (
                  <BodyRow key={user._id || user.email}>
                    <BodyCell>
                      {user.name && (
                        <CellEllipsis title={user.name} style={{ fontWeight: 600, color: COLORS.primaryDeep }}>{user.name}</CellEllipsis>
                      )}
                      <CellEllipsis title={user.email || ''} style={user.name ? { fontSize: TYPE.caption, color: COLORS.muted } : undefined}>
                        {user.email || '—'}
                      </CellEllipsis>
                      {user.phone && (
                        <CellEllipsis style={{ fontSize: TYPE.caption, color: COLORS.muted }}>{user.phone}</CellEllipsis>
                      )}
                    </BodyCell>
                    <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                      <StatusChip>{humanizeStatus(user.userType) || '—'}</StatusChip>
                    </BodyCell>
                    <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                      <StatusChip bg={chip.bg} color={chip.color}>{statusLabel}</StatusChip>
                    </BodyCell>
                    <BodyCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
                        {busy && <CircularProgress size={16} sx={{ color: COLORS.primary }} />}
                        {actions(user, busy)}
                      </Box>
                    </BodyCell>
                  </BodyRow>
                );
              })}
            </TableBody>
            {totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <Pagination
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePrevPage={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    handleNextPage={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                  />
                </TableRow>
              </TableFooter>
            )}
          </StyledTable>
        </TableScroll>
      </TableCard>
    );
  };

  const emptyCard = (content) => <TableCard>{content}</TableCard>;

  const listsUnavailable = emptyCard(
    <StateBlock message="This list is loaded at sign-in and is not available after a page refresh. Sign in again to see it." />
  );

  const statPending = (
    <StatCard gradient="linear-gradient(135deg, #6750A4, #4F378B)" shadow="rgba(103,80,164,0.32)">
      <StatText>
        <StatNumber>{pendingLoading && pendingUsers.length === 0 ? '—' : pendingUsers.length}</StatNumber>
        <StatLabel>Pending Review</StatLabel>
      </StatText>
      <StatIcon><PendingActionsIcon /></StatIcon>
    </StatCard>
  );
  const statApproved = (
    <StatCard gradient="linear-gradient(135deg, #2E9B54, #1B6B3A)" shadow="rgba(46,155,84,0.28)">
      <StatText>
        <StatNumber>{count(approvedUsers)}</StatNumber>
        <StatLabel>Approved</StatLabel>
      </StatText>
      <StatIcon><CheckCircleIcon /></StatIcon>
    </StatCard>
  );
  const statRejected = (
    <StatCard gradient="linear-gradient(135deg, #E91E8C, #C2185B)" shadow="rgba(233,30,140,0.28)">
      <StatText>
        <StatNumber>{count(rejectedUsers)}</StatNumber>
        <StatLabel>Rejected</StatLabel>
      </StatText>
      <StatIcon><CancelIcon /></StatIcon>
    </StatCard>
  );

  const navItem = (tab, Icon, label) => (
    <NavButton $active={activeTab === tab} onClick={() => switchTab(tab)}>
      <Icon />
      <span>{label}</span>
    </NavButton>
  );

  if (!email) return null;

  return (
    <ThemeProvider theme={recruiterTheme}>
      <GlobalStyle />

      {/* ── Toast Notifications ── */}
      <ToastWrapper>
        {toasts.map(t => (
          <Toast key={t.id} type={t.type} $exiting={t.exiting}>
            {t.type === 'success' ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
            <span style={{ flex: 1 }}>{t.message}</span>
            <ToastClose size="small" onClick={() => removeToast(t.id)}>
              <CloseIcon />
            </ToastClose>
          </Toast>
        ))}
      </ToastWrapper>

      <Container>
        <CssBaseline />

        {/* ── AppBar ── */}
        <StyledAppBar position="fixed">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 72, padding: '0 24px' }}>

            {/* Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '24px', borderRight: '1.5px solid rgba(103,80,164,0.1)' }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: `${RADIUS.md}px`,
                background: 'linear-gradient(135deg, #EDE7F6, #D0BCFF)',
                border: '2px solid rgba(103,80,164,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(79,55,139,0.18)', flexShrink: 0,
              }}>
                <img src={logo} alt="Logo" style={{ height: 36, width: 36, borderRadius: RADIUS.sm }} />
              </Box>
              <Box>
                <BrandText>Resourcing Bot</BrandText>
                <Typography sx={{
                  fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700,
                  color: COLORS.muted, letterSpacing: '0.08em', textTransform: 'uppercase',
                  lineHeight: 1.25, mt: '4px',
                }}>
                  Admin Panel
                </Typography>
              </Box>
            </Box>

            {/* Right — User card */}
            {userData && (
              <UserCard>
                <StyledAvatar>{(userData.name || userData.email || '?').charAt(0).toUpperCase()}</StyledAvatar>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, color: COLORS.primaryDeep, lineHeight: 1.25 }}>
                    {userData.name || userData.email}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 500, fontSize: TYPE.caption, color: COLORS.muted, lineHeight: 1.25, textTransform: 'capitalize' }}>
                    {humanizeStatus(userData.userType)}
                  </Typography>
                </Box>
              </UserCard>
            )}
          </Toolbar>
        </StyledAppBar>

        {/* ── Sidebar ── */}
        <MenuContainer>
          <SidebarLabel>Admin Tools</SidebarLabel>
          {navItem('dashboard', WidgetsIcon, 'Admin Dashboard')}
          {navItem('approved', CheckCircleIcon, 'Approved Users')}
          {navItem('rejected', CancelIcon, 'Rejected Users')}
          {navItem('pendingData', PendingActionsIcon, 'Pending Actions')}

          <SidebarDivider />
          <SidebarLabel>Reporting</SidebarLabel>
          {navItem('report', BarChartIcon, 'Reports')}
          {navItem('activityLogs', AssignmentIcon, 'Activity Logs')}

          <SidebarDivider />
          <SidebarLabel>Account</SidebarLabel>
          {navItem('profile', PersonIcon, 'Profile')}
          <NavButton onClick={handleLogout}>
            <ExitToAppIcon />
            <span>Logout</span>
          </NavButton>
        </MenuContainer>

        {/* ── Main Content ── */}
        <CenteredSection>
          <ScrollableContent>

            {/* ── Admin Dashboard (Pending Users) ── */}
            {activeTab === 'dashboard' && (
              <StyledPaper>
                <PageTitle>Admin Dashboard</PageTitle>
                <PageSubtitle>Review and manage user role requests</PageSubtitle>
                <StatGrid>
                  {statPending}
                  {statApproved}
                  {statRejected}
                </StatGrid>

                {pendingLoading && pendingUsers.length === 0 ? (
                  emptyCard(<StateBlock loading message="Loading pending users…" />)
                ) : pendingError && pendingUsers.length === 0 ? (
                  emptyCard(<StateBlock error message={pendingError} onRetry={() => fetchPendingUsers()} />)
                ) : pendingUsers.length === 0 ? (
                  emptyCard(<StateBlock message="No pending users. All caught up!" />)
                ) : (
                  renderTable('Users awaiting review', pageRows, 'Under Review', (user, busy) => (
                    <>
                      <ActionButton size="small" disabled={busy} onClick={() => decide(user.email, 'approve')}>
                        Approve
                      </ActionButton>
                      <ActionButton size="small" $danger disabled={busy} onClick={() => decide(user.email, 'reject')}>
                        Reject
                      </ActionButton>
                    </>
                  ))
                )}
              </StyledPaper>
            )}

            {/* ── Approved Users ── */}
            {activeTab === 'approved' && (
              <StyledPaper>
                <PageTitle>Approved Users</PageTitle>
                <PageSubtitle>Users with active, approved roles</PageSubtitle>
                <StatGrid>{statApproved}</StatGrid>
                {!listsKnown ? listsUnavailable : approvedUsers.length === 0 ? (
                  emptyCard(<StateBlock message="No approved users yet." />)
                ) : (
                  renderTable('All approved users', pageRows, 'Approved', (user, busy) => (
                    <ActionButton size="small" $danger disabled={busy} onClick={() => decide(user.email, 'reject')}>
                      Reject
                    </ActionButton>
                  ))
                )}
              </StyledPaper>
            )}

            {/* ── Rejected Users ── */}
            {activeTab === 'rejected' && (
              <StyledPaper>
                <PageTitle>Rejected Users</PageTitle>
                <PageSubtitle>Users whose role requests were declined</PageSubtitle>
                <StatGrid>{statRejected}</StatGrid>
                {!listsKnown ? listsUnavailable : rejectedUsers.length === 0 ? (
                  emptyCard(<StateBlock message="No rejected users." />)
                ) : (
                  renderTable('All rejected users', pageRows, 'Rejected', (user, busy) => (
                    <ActionButton size="small" disabled={busy} onClick={() => decide(user.email, 'approve')}>
                      Approve
                    </ActionButton>
                  ))
                )}
              </StyledPaper>
            )}

            {/* ── Pending Actions ── */}
            {activeTab === 'pendingData' && userData && (
              <StyledPaper>
                <PendingData
                  key={tabRefreshKey}
                  email={userData.email}
                  userType={userData.userType}
                  onBack={() => switchTab('dashboard')}
                />
              </StyledPaper>
            )}

            {/* ── Reports ── */}
            {activeTab === 'report' && (
              <StyledPaper>
                <PageTitle>Reports</PageTitle>
                <PageSubtitle>Analytics and insights for your platform</PageSubtitle>
                <Reports key={tabRefreshKey} />
              </StyledPaper>
            )}

            {/* ── Activity Logs (renders its own header) ── */}
            {activeTab === 'activityLogs' && (
              <StyledPaper>
                <ActivityLogs key={tabRefreshKey} />
              </StyledPaper>
            )}

            {/* ── Profile (pads itself) ── */}
            {activeTab === 'profile' && (
              <ProfileForm key={tabRefreshKey} userData={userData} onProfileUpdated={setProfileOverride} />
            )}

          </ScrollableContent>
        </CenteredSection>
      </Container>
    </ThemeProvider>
  );
}

export default AdminDashboard;
